import React, { useCallback } from "react";
import { StripeExpressReturnTo } from "@common/schemas/domains/stripe/stripe-express.schema";
import { Button } from "@design-system/Button/Button";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useStripeExpressRedirect } from "@hooks/useStripeExpressRedirect";
import { useRemintStripeExpressMutation } from "@mutations/stripeConnection";

interface Props {
    connectionId: number;
    returnTo: StripeExpressReturnTo;
    invoiceId?: string;
    className?: string;
    children?: React.ReactNode;
}

/**
 * "Finish setup" CTA for an Incomplete Express connection. Remints an Account
 * Link and leaves the app for Stripe-hosted KYC. Because the redirect leaves the
 * app, View-only is refused here rather than relying on the server's 403.
 */
export const FinishStripeExpressSetup: React.FC<Props> = ({
    connectionId,
    returnTo,
    invoiceId,
    className,
    children = "Finish setup",
}) => {
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const redirectToStripe = useStripeExpressRedirect();
    const mutation = useRemintStripeExpressMutation();

    const handleClick = useCallback(async () => {
        if (refuseIfViewOnly()) {
            return;
        }
        await redirectToStripe(() =>
            mutation.mutateAsync({ connectionId, returnTo, invoiceId }),
        );
    }, [
        refuseIfViewOnly,
        redirectToStripe,
        mutation,
        connectionId,
        returnTo,
        invoiceId,
    ]);

    return (
        <Button
            variant="secondary"
            size="sm"
            className={className}
            disabled={mutation.isPending}
            onClick={handleClick}
        >
            {children}
        </Button>
    );
};
